import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import AdminHeaderBar from "../../components/adminHeaderBar/adminHeaderBar";
import { usersData$ } from "../../reduxStore/selectors/userSelectors";
import styles from "../../publicAsset/adminPage.module.scss";

const overviewCards = [
    { label: "Open support items", value: "18", hint: "Prioritize billing and access issues" },
    { label: "Pending content checks", value: "42", hint: "Question bank review queue" },
    { label: "Active subscriptions", value: "1,284", hint: "+12% from last month" },
    { label: "System uptime", value: "99.9%", hint: "All core services healthy" }
];

const quickActions = [
    {
        icon: "👥",
        title: "User management",
        description: "Review admin permissions, locked accounts, and onboarding trends.",
        status: "Ready"
    },
    {
        icon: "📝",
        title: "Question bank",
        description: "Track new uploads, moderation, and publishing quality checks.",
        status: "Draft"
    },
    {
        icon: "💳",
        title: "Billing control",
        description: "Monitor purchases, refund requests, and package performance.",
        status: "Ready"
    },
    {
        icon: "🚩",
        title: "Feature rollout",
        description: "Coordinate experiments and staged releases for new learning tools.",
        status: "Admin"
    }
];

const queueItems = [
    { title: "3 refund requests need review", meta: "Payments • updated 12 minutes ago", tag: "High" },
    { title: "7 new questions awaiting approval", meta: "Content • today", tag: "Review" },
    { title: "2 announcements scheduled", meta: "Comms • tomorrow morning", tag: "Planned" }
];

const systemItems = [
    { title: "Backend API", meta: "Operational" },
    { title: "Chatbot service", meta: "Stable" },
    { title: "Payment flow", meta: "Monitoring enabled" }
];

export default function AdminPage() {
    const userData = useSelector(usersData$);
    const adminName =
        [userData?.first_name, userData?.last_name].filter(Boolean).join(" ") ||
        userData?.full_name ||
        userData?.email ||
        "Admin";

    return (
        <>
            <Helmet>
                <title>Admin Portal | Prepal</title>
                <meta name="description" content="Admin control center for Prepal operations and moderation." />
            </Helmet>

            <div className={styles.adminPage}>
                <AdminHeaderBar />

                <section id="overview" className={styles.heroSection}>
                    <div className={styles.heroCopy}>
                        <span className={styles.badge}>Admin only</span>
                        <h1>Prepal admin control center</h1>
                        <p>
                            A first-pass portal layout for managing learners, content, billing, and feature rollouts
                            while staying aligned with the current Prepal brand language and blue-forward palette.
                        </p>
                    </div>

                    <div className={styles.heroPanel}>
                        <span className={styles.metaLabel}>Signed in as</span>
                        <strong>{adminName}</strong>

                        <ul className={styles.heroList}>
                            <li>Protected for admin-role accounts only</li>
                            <li>Uses the same dashboard shell and design system</li>
                            <li>Ready for future management modules</li>
                        </ul>
                    </div>
                </section>

                <section className={styles.overviewSection}>
                    {overviewCards.map((card) => (
                        <article key={card.label} className={styles.statCard}>
                            <p className={styles.statLabel}>{card.label}</p>
                            <h2 className={styles.statValue}>{card.value}</h2>
                            <p className={styles.statHint}>{card.hint}</p>
                        </article>
                    ))}
                </section>

                <div className={styles.contentGrid}>
                    <div className={styles.mainColumn}>
                        <section id="modules" className={styles.card}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h2>Management modules</h2>
                                    <p>Core admin areas prepared for the next implementation phase.</p>
                                </div>
                                <span className={styles.statusPill}>Layout draft</span>
                            </div>

                            <div className={styles.quickActionGrid}>
                                {quickActions.map((item) => (
                                    <article key={item.title} className={styles.actionCard}>
                                        <div className={styles.actionContent}>
                                            <div className={styles.actionTop}>
                                                <div className={styles.actionIcon}>{item.icon}</div>
                                                <span className={styles.actionStatus}>{item.status}</span>
                                            </div>
                                            <h3>{item.title}</h3>
                                            <p>{item.description}</p>
                                        </div>

                                        <div className={styles.actionOverlay}>
                                            <span className={styles.actionOverlayText}>
                                                Go to {item.title}
                                                <span className={styles.actionArrow}>→</span>
                                            </span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>

                        <section id="queue" className={styles.tableCard}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h2>Recent admin queue</h2>
                                    <p>Example operational tasks to anchor the portal layout.</p>
                                </div>
                                <span className={styles.statusPill}>Today</span>
                            </div>

                            <div className={styles.queueList}>
                                {queueItems.map((item) => (
                                    <div key={item.title} className={styles.queueItem}>
                                        <div>
                                            <p className={styles.queueTitle}>{item.title}</p>
                                            <p className={styles.queueMeta}>{item.meta}</p>
                                        </div>
                                        <span className={styles.queueTag}>{item.tag}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <aside className={styles.sideColumn}>
                        <section id="system" className={styles.sideCard}>
                            <h3>System snapshot</h3>
                            <div className={styles.systemList}>
                                {systemItems.map((item) => (
                                    <div key={item.title} className={styles.systemItem}>
                                        <div>
                                            <p className={styles.systemTitle}>{item.title}</p>
                                            <p className={styles.systemMeta}>{item.meta}</p>
                                        </div>
                                        <span className={styles.queueTag}>Healthy</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className={styles.sideCard}>
                            <h3>Launch checklist</h3>
                            <div className={styles.checklist}>
                                <div className={styles.checkItem}><span className={styles.checkDot} />Admin route protected</div>
                                <div className={styles.checkItem}><span className={styles.checkDot} />Sidebar access visible only to admins</div>
                                <div className={styles.checkItem}><span className={styles.checkDot} />Brand colors and card styling applied</div>
                            </div>
                        </section>

                        <section className={`${styles.sideCard} ${styles.noteCard}`}>
                            <h3>Next step</h3>
                            <p>
                                This page is now ready to be connected to real admin modules such as user management,
                                question moderation, payment analytics, and announcements.
                            </p>
                        </section>
                    </aside>
                </div>
            </div>
        </>
    );
}
